import React, { useState, useEffect, useContext, useCallback } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import AddProjectModal from "../components/AddProjectModel";
import EditProjectModal from "../components/EditProjectModal";
import { NotificationsContext } from "../context/NotificationsContext";
import "./Project.css";
import getApiUrl from "../utils/api";
import { usePostHog } from "posthog-js/react";

const Project = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [userId, setUserId] = useState(null);
  const { addNotification } = useContext(NotificationsContext);
  const posthog = usePostHog();

  // Fetch user ID on component mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(getApiUrl("api/auth/me"), {
          credentials: "include",
        });

        if (response.ok) {
          const userData = await response.json();
          const userId =
            userData.user?.userId ||
            userData.user?.id ||
            userData.userId ||
            userData.id;

          if (userId) {
            setUserId(userId);
          }
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  // Fetch projects from backend
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl("api/project"), {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      } else {
        const errorData = await response.json();
        throw new Error(
          `Failed to fetch projects: ${errorData.message || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      addNotification({
        type: "error",
        title: "Error",
        message: "Failed to load projects",
      });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  // Fetch projects when userId is available
  useEffect(() => {
    if (userId) {
      fetchProjects();
    }
  }, [userId, fetchProjects]);

  // Handle project creation
  const handleAddProject = async (projectData) => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl("api/project"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(projectData),
      });

      if (response.ok) {
        addNotification({
          type: "success",
          title: "Success",
          message: "Project created successfully!",
        });

        posthog.capture('Project Created', {
          name: projectData.name,
        })

        fetchProjects(); // Refresh the projects list
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create project");
      }
    } catch (error) {
      console.error("Error creating project:", error);
      addNotification({
        type: "error",
        title: "Error",
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle project editing
  const handleEditProject = (project) => {
    setEditingProject(project);
    setShowEditModal(true);
  };

  // Handle project update
  const handleUpdateProject = async (projectId, projectData) => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(`api/project/${projectId}`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(projectData),
      });

      if (response.ok) {
        addNotification({
          type: "success",
          title: "Success",
          message: "Project updated successfully!",
        });

        posthog.capture('Project Updated', {
          name: projectData.name,
        })
        fetchProjects(); // Refresh the projects list
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update project");
      }
    } catch (error) {
      console.error("Error updating project:", error);
      addNotification({
        type: "error",
        title: "Error",
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle project deletion
  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) {
      return;
    }

    try {
      setDeleteLoadingId(projectId);
      const response = await fetch(getApiUrl(`api/project/${projectId}`), {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        addNotification({
          type: "success",
          title: "Success",
          message: "Project deleted successfully!",
        });
        fetchProjects(); // Refresh the projects list
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete project");
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      addNotification({
        type: "error",
        title: "Error",
        message: error.message,
      });
    } finally {
      setDeleteLoadingId(null);
    }
  };

  // Filter projects based on search query
  const filteredProjects = projects.filter((project) => {
    const query = searchQuery.toLowerCase();
    const nameMatch = project.name.toLowerCase().includes(query);
    const departmentMatch = project.department?.name
      ?.toLowerCase()
      ?.includes(query);
    const descriptionMatch = project.description
      ?.toLowerCase()
      ?.includes(query);
    const ownerMatch = project.ownerName?.toLowerCase()?.includes(query);

    return nameMatch || departmentMatch || descriptionMatch || ownerMatch;
  });

  return (
    <DashboardLayout>
      <div className="project-container">
        <div className="project-header">
          <div>
            <h2 className="project-title">Projects</h2>
            <div className="project-subtitle">Managing your Projects here.</div>
          </div>
          <div className="project-actions">
            <button
              className="taskboard-btn"
              onClick={() => setShowAddModal(true)}
            >
              Create New Project
            </button>
          </div>
        </div>
        <div className="search-container">
          <label className="search-label">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search projects..."
              className="search-input"
            />
          </label>
        </div>
        <div className="projects-table-container">
          <table className="projects-table">
            <thead>
              <tr>
                <th>Project Name</th>
                <th>Department</th>
                <th>Description</th>
                <th>Access</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="loading-state">
                    Loading projects...
                  </td>
                </tr>
              ) : filteredProjects.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty-state">
                    {searchQuery
                      ? "No projects found matching your search."
                      : "No projects created yet. Create your first project!"}
                  </td>
                </tr>
              ) : (
                (() => {
                  return filteredProjects.map((project) => (
                    <tr key={project.id}>
                      <td className="project-name">{project.name}</td>
                      <td>{project.department?.name || "N/A"}</td>
                      <td className="project-description">
                        {project.description || "No description"}
                      </td>
                      <td className="project-access">
                        {(() => {
                          const isOwner = Boolean(project.isOwner);
                          const membershipRole = (
                            project.membershipRole || "Member"
                          ).toString();
                          const chipLabel = isOwner
                            ? "Owner"
                            : membershipRole.charAt(0).toUpperCase() +
                            membershipRole.slice(1);
                          const ownerName = project.ownerName?.trim()
                            ? project.ownerName
                            : "—";

                          let joinedLabel = null;
                          if (!isOwner && project.membershipJoinedAt) {
                            try {
                              const joinedDate = new Date(
                                project.membershipJoinedAt
                              );
                              if (!isNaN(joinedDate.getTime())) {
                                joinedLabel = `Joined ${joinedDate.toLocaleDateString()}`;
                              }
                            } catch (e) {
                              joinedLabel = null;
                            }
                          }

                          return (
                            <div className="access-cell">
                              <span
                                className={`access-chip ${isOwner ? "owner" : "member"
                                  }`}
                              >
                                {chipLabel}
                              </span>
                              <div className="access-meta">
                                <span className="access-owner">
                                  Owner: {ownerName}
                                </span>
                                {joinedLabel && (
                                  <span className="access-joined">
                                    {joinedLabel}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        {project.isOwner ? (
                          <div className="project-actions-cell">
                            <button
                              onClick={() => handleEditProject(project)}
                              className="edit-btn"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDeleteProject(project.id)}
                              className="delete-btn"
                              disabled={deleteLoadingId === project.id}
                            >
                              {deleteLoadingId === project.id ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                  <div className="table-loader" style={{ width: "12px", height: "12px", borderWidth: "2px" }} />
                                  Deleting...
                                </div>
                              ) : (
                                <>🗑️ Delete</>
                              )}
                            </button>
                          </div>
                        ) : (
                          <span className="action-disabled">Owner managed</span>
                        )}
                      </td>
                    </tr>
                  ));
                })()
              )}
            </tbody>
          </table>
        </div>
        <div className="projects-count">
          Showing {filteredProjects.length} of {projects.length} projects
        </div>
        <AddProjectModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAddProject={handleAddProject}
        />
        <EditProjectModal
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingProject(null);
          }}
          onUpdateProject={handleUpdateProject}
          project={editingProject}
        />
      </div>
    </DashboardLayout>
  );
};

export default Project;
